import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import Select from "react-select";
import { toast } from "react-toastify";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SearchIcon from "@mui/icons-material/Search";
import DOMPurify from "dompurify";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  Button,
  TextField,
  Chip,
  Paper,
  Grid,
  CircularProgress,
  Skeleton,
} from "@mui/material";

import { NewJobAction } from "../../../../Redux/action/NewJobAction";
// import { CandidateWorkPreferenceAction } from "../../../../Redux/action/CandidateWorkPreferenceAction";
// import { ApplyJobAction } from "../../../../Redux/action/ApplyJobAction";
import { CandidateProfile } from "../../../../Redux/action/CandidateProfileAction";
import { fetchSkillDetails } from "../../../../Redux/action/MasterSkillAction";
import Pagination from "../../../Common/Pagination/Pagination";

const PRIMARY = "#286a94";

const NewJobs = () => {
  const { userResult } = useSelector((state) => state.Auth);
  const userInfo = useSelector((state) => state?.JobList?.result?.content);
  const totalPages = useSelector((state) => state?.JobList?.result?.totalPages);
  const AllSkillList = useSelector(
    (state) => state?.MasterTableDetails?.skill?.content
  );
  const candidateInfo = useSelector((state) => state?.Profile?.result);
  const jobsLoading = useSelector((state) => state?.JobList?.loading);
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [skillsFromCI, setSkillsfromCI] = useState([]);
  const [pageNumber, setPageNumber] = useState(0);
  const [reRender, setReRender] = useState(false);

  const candidateId = userResult?.userid;

  const options = AllSkillList?.map((item) => ({
    value: item?.skillName,
    label: item?.skillName,
  }));

  const handleSkillsChange = (selectedOptions) => {
    if (selectedOptions.length === 0) {
      return toast.error("Minimum 1 skill is required");
    }

    setSkillsfromCI(selectedOptions);

    dispatch(
      NewJobAction({
        candidateId,
        pageNumber,
        pageSize: 10,
        filter: "ALL",
        search,
        skills: selectedOptions.map((o) => o.value),
      })
    );
  };

  const handlePageChange = (page) => setPageNumber(page);

  // const handleApply = (e, jobId) => {
  //   e.stopPropagation();
  //   setApplyingJobId(jobId);

  //   dispatch(
  //     ApplyJobAction({
  //       candidateId: [userResult?.userid],
  //       jobId,
  //     })
  //   );
  //   setApplyingJobId(null);
  //   navigate("/new_job");
  // };

  useEffect(() => {
    dispatch(fetchSkillDetails());
    dispatch(CandidateProfile(candidateId));
  }, []);

  useEffect(() => {
    if (candidateInfo) {
      setSkillsfromCI(
        candidateInfo?.skillList?.map((s) => ({ label: s, value: s })) || []
      );

      dispatch(
        NewJobAction({
          candidateId,
          pageNumber,
          pageSize: 10,
          filter: "ALL",
          search,
          skills: candidateInfo?.skillList,
        })
      );

      // dispatch(CandidateWorkPreferenceAction(candidateId));
    }
  }, [candidateInfo, pageNumber, search, reRender]);

  return (
    <Box sx={{ maxWidth: "1250px", mx: "auto", mt: 2, px: 2 }}>
      {/* ---- Filters ---- */}
      <Box sx={{ mb: 4 }}>
        {/* ---- Header ---- */}
        <Typography
          variant="h5"
          sx={{ fontWeight: 700, color: PRIMARY, mb: 1 }}
        >
          🔎 Find Your Next Opportunity
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: "#6b7280",
            mb: 3,
            maxWidth: "650px",
            lineHeight: 1.6,
          }}
        >
          Search and filter through job openings that align with your skills,
          experience, and interest — and apply with confidence.
        </Typography>

        {/* ---- Filter Area ---- */}
        <Paper
          elevation={3}
          sx={{
            p: 3,
            borderRadius: "14px",
            border: `1px solid ${PRIMARY}30`,
            background: "#f9fbff",
          }}
        >
          <Grid container spacing={2} alignItems="center">
            {/* Search Box */}
            <Grid item xs={12} md={6}>
              <TextField
                placeholder="Search job..."
                size="medium"
                fullWidth
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <SearchIcon
                      sx={{ color: "#9ba7b0", fontSize: 22, mr: 1 }}
                    />
                  ),
                }}
                sx={{
                  background: "white",
                  borderRadius: "12px",
                  transition: "0.25s",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "12px",
                    paddingLeft: "10px",
                    "& fieldset": {
                      borderColor: "#d3dce6",
                      borderWidth: "1px",
                    },
                    "&:hover fieldset": {
                      borderColor: PRIMARY,
                    },
                    "&.Mui-focused fieldset": {
                      borderColor: PRIMARY,
                      boxShadow: `0 0 8px ${PRIMARY}40`,
                      borderWidth: "2px",
                    },
                  },
                  "& input": {
                    fontSize: "22px",
                    padding: "15px 0",
                    outline: "none",
                    border: "none",
                    boxShadow: "none",
                  },
                }}
              />
            </Grid>

            {/* Skills Dropdown */}
            {skillsFromCI.length >= 1 && (
              <Grid item xs={12} md={6}>
                <Select
                  options={options}
                  isMulti
                  value={skillsFromCI}
                  onChange={handleSkillsChange}
                  placeholder="🎯 Filter by required skills"
                  styles={{
                    control: (base, state) => ({
                      ...base,
                      borderRadius: "10px",
                      padding: "2px",
                      background: "white",
                      borderColor: state.isFocused ? PRIMARY : "#d3dce6",
                      boxShadow: state.isFocused
                        ? `0 0 6px ${PRIMARY}50`
                        : "none",
                      transition: "0.25s",
                      "&:hover": {
                        borderColor: PRIMARY,
                      },
                    }),

                    multiValue: (base) => ({
                      ...base,
                      backgroundColor: `${PRIMARY}15`,
                      borderRadius: "6px",
                    }),

                    multiValueLabel: (base) => ({
                      ...base,
                      color: PRIMARY,
                      fontWeight: 600,
                    }),

                    multiValueRemove: (base) => ({
                      ...base,
                      color: PRIMARY,
                      cursor: "pointer",
                      ":hover": {
                        background: PRIMARY,
                        color: "white",
                      },
                    }),
                  }}
                />
              </Grid>
            )}
          </Grid>
        </Paper>
      </Box>

      {/* ---- Job List ---- */}
      <Grid container spacing={3} sx={{ mt: 3 }}>
        <Grid item xs={12} md={8}>
          {/* Loader */}
          {jobsLoading ? (
            [...Array(4)].map((_, i) => (
              <Card
                key={i}
                sx={{
                  mb: 3,
                  p: 2.5,
                  display: "flex",
                  gap: 2,
                  alignItems: "center",
                  borderRadius: 3,
                }}
              >
                <CircularProgress size={35} sx={{ color: "#286a94" }} />

                {/* Skeleton Content */}
                <Box sx={{ flex: 1 }}>
                  <Skeleton variant="text" width="55%" height={22} />
                  <Skeleton
                    variant="text"
                    width="85%"
                    height={16}
                    sx={{ mt: 1 }}
                  />
                  <Skeleton
                    variant="text"
                    width="45%"
                    height={16}
                    sx={{ mt: 1 }}
                  />
                  <Skeleton
                    variant="rounded"
                    width="30%"
                    height={30}
                    sx={{ mt: 2, borderRadius: "10px" }}
                  />
                </Box>
              </Card>
            ))
          ) : userInfo?.length > 0 ? (
            userInfo.map((item) => (
              <Card
                key={item.jobId}
                onClick={() => navigate(`/view_job/${item.jobId}`)}
                sx={{
                  mb: 3,
                  cursor: "pointer",
                  borderRadius: "16px",
                  borderLeft: "6px solid #286a94",
                  boxShadow: "0 6px 18px rgba(40,106,148,0.12)",
                  transition: "all 0.25s ease",
                  "&:hover": {
                    transform: "translateY(-3px)",
                    boxShadow: "0 14px 30px rgba(40,106,148,0.2)",
                  },
                }}
              >
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box
                    display="flex"
                    flexDirection={{ xs: "row", sm: "row" }}
                    alignItems={{ xs: "flex-start", sm: "center" }}
                    justifyContent="space-between"
                    gap={1}
                    mb={1}
                  >
                    <Box display="flex" flexDirection="column" gap={0.5}>
                      {/* Job Title */}
                      <Typography
                        variant="h6"
                        fontWeight={800}
                        sx={{
                          color: "#0f3d5f",
                          fontSize: { xs: "16px", sm: "18px" },
                          wordBreak: "break-word",
                        }}
                      >
                        {item.jobTitle}
                      </Typography>

                      {/* Job ID  */}
                      <Chip
                        label={`JOB ID: ${item?.jobUuid}`}
                        size="small"
                        sx={{
                          bgcolor: "#e9f4fb",
                          color: "#286a94",
                          fontWeight: 700,
                          borderRadius: "999px",
                          fontSize: "11px",
                          px: 1.2,
                          width: "fit-content",
                          border: "1px solid #c7e1f3",
                        }}
                      />
                    </Box>

                    {/* View Job button */}
                    <Chip
                      label="View Job →"
                      size="small"
                      clickable
                      sx={{
                        bgcolor: "#286a94",
                        color: "#ffffff",
                        fontWeight: 600,
                        borderRadius: "10px",
                        px: 1.5,
                        alignSelf: { xs: "flex-start", sm: "center" },
                        "&:hover": {
                          bgcolor: "#1f5678",
                        },
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/view_job/${item.jobId}`);
                      }}
                    />
                  </Box>

                  {/* ---------- DESCRIPTION ---------- */}
                  <Typography
                    sx={{
                      color: "#475569",
                      fontSize: { xs: "13px", sm: "14px" },
                      lineHeight: 1.7,
                      mb: 2,
                    }}
                  >
                    {/* {item.jobDescription?.length > 220
                      ? `${item.jobDescription.slice(0, 220)}...`
                      : item.jobDescription} */}

                    <p
                      className="eBench-job-description mb-4 mt-4"
                      dangerouslySetInnerHTML={{
                        __html: DOMPurify.sanitize(
                          item.jobDescription?.length > 220
                            ? `${item.jobDescription.slice(0, 220)}...`
                            : item.jobDescription
                        ),
                      }}
                    ></p>
                  </Typography>

                  <Grid container spacing={1.5}>
                    <Grid item xs={12} sm={4}>
                      <Chip
                        icon={
                          <LocationOnIcon
                            sx={{ color: "#286a94", fontSize: 18 }}
                          />
                        }
                        label={item.jobLocation || item.workType}
                        sx={{
                          bgcolor: "#f0f7fc",
                          color: "#286a94",
                          fontWeight: 600,
                          borderRadius: "12px",
                          px: 1.5,
                          height: 32,
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          border: "1px solid #d5eaf8",
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Chip
                        icon={
                          <AccessTimeIcon
                            sx={{ color: "#286a94", fontSize: 18 }}
                          />
                        }
                        label={(() => {
                          const min = Number(
                            String(item.minimumExperience || "").replace(
                              /\D/g,
                              ""
                            )
                          );
                          const max = Number(
                            String(item.maximumExperience || "").replace(
                              /\D/g,
                              ""
                            )
                          );
                          if (isNaN(min) || isNaN(max))
                            return "Experience: N/A";
                          if (min === 0 && max > 0)
                            return `Fresher to ${max} years`;
                          if (min === 0 && max === 0) return "Fresher";
                          if (min === max) return `${min} years`;
                          return `${min} to ${max} years`;
                        })()}
                        sx={{
                          bgcolor: "#e9f4fb",
                          color: "#286a94",
                          fontWeight: 600,
                          borderRadius: "12px",
                          px: 1.5,
                          height: 32,
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          border: "1px solid #c7e1f3",
                        }}
                      />
                    </Grid>

                    <Grid item xs={12} sm={4}>
                      <Chip
                        icon={
                          <CalendarMonthIcon
                            sx={{ color: "#286a94", fontSize: 18 }}
                          />
                        }
                        label={`Posted on: ${
                          item?.postedDate
                            ? item?.postedDate?.split(" ")[0]
                            : "N/A"
                        }`}
                        sx={{
                          bgcolor: "#f0f7fc",
                          color: "#286a94",
                          fontWeight: 600,
                          borderRadius: "12px",
                          px: 1.5,
                          height: 32,
                          display: "flex",
                          justifyContent: "flex-start",
                          alignItems: "center",
                          border: "1px solid #d5eaf8",
                        }}
                      />
                    </Grid>
                  </Grid>

                  {/* ---------- SKILLS ---------- */}
                  {item?.requiredSkills?.length > 0 && (
                    <Box sx={{ mt: 2 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          fontSize: "13px",
                          mb: 1,
                          color: "#0f3d5f",
                        }}
                      >
                        Required Skills
                      </Typography>

                      <Box display="flex" flexWrap="wrap" gap={1}>
                        {item.requiredSkills.map((skill, i) => (
                          <Chip
                            key={i}
                            label={skill}
                            size="small"
                            sx={{
                              bgcolor: "#f0f7fc",
                              color: "#286a94",
                              fontWeight: 600,
                              borderRadius: "999px",
                              fontSize: "11px",
                              border: "1px solid #d5eaf8",
                            }}
                          />
                        ))}
                      </Box>
                    </Box>
                  )}
                </CardContent>
              </Card>
            ))
          ) : (
            <Typography sx={{ textAlign: "center", mt: 5, color: "#999" }}>
              ❌ No jobs found matching your profile.
            </Typography>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Paper
            sx={{
              p: 3,
              borderRadius: "14px",
              background: "#f9fbff",
              border: "1px solid #d6e4f0",
            }}
          >
            {/* Title */}
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: PRIMARY,
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}
            >
              💡 Smart Job Tips
            </Typography>

            <Divider sx={{ my: 2 }} />

            {/* List Styling */}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              <Box sx={{ display: "flex", gap: 1.5 }}>
                <span style={{ fontSize: "18px" }}>🚀</span>
                <Typography sx={{ fontSize: "14px", color: "#4b5563" }}>
                  Apply to{" "}
                  <strong style={{ color: PRIMARY }}>
                    multiple relevant jobs
                  </strong>{" "}
                  — it significantly increases your chances of getting
                  shortlisted.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5 }}>
                <span style={{ fontSize: "18px" }}>📄</span>
                <Typography sx={{ fontSize: "14px", color: "#4b5563" }}>
                  Keep your resume{" "}
                  <strong style={{ color: PRIMARY }}>
                    updated & optimized
                  </strong>
                  . Make sure it reflects your latest skills and experience.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5 }}>
                <span style={{ fontSize: "18px" }}>🎯</span>
                <Typography sx={{ fontSize: "14px", color: "#4b5563" }}>
                  Match your{" "}
                  <strong style={{ color: PRIMARY }}>skills and filters</strong>{" "}
                  with the role to improve ranking.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5 }}>
                <span style={{ fontSize: "18px" }}>⭐</span>
                <Typography sx={{ fontSize: "14px", color: "#4b5563" }}>
                  Save roles you're interested in and revisit them when ready.
                </Typography>
              </Box>

              <Box sx={{ display: "flex", gap: 1.5 }}>
                <span style={{ fontSize: "18px" }}>🤝</span>
                <Typography sx={{ fontSize: "14px", color: "#4b5563" }}>
                  Engage with hiring teams quickly after applying — it shows
                  seriousness and professionalism.
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Pagination */}
      {totalPages > 0 && (
        <Box sx={{ mt: 3 }}>
          <Pagination
            totalPages={totalPages}
            currentPage={pageNumber}
            onPageChange={handlePageChange}
          />
        </Box>
      )}
    </Box>
  );
};

export default NewJobs;
